import React, {FC, useEffect, useRef, useState} from "react";
import {DocumentNode, useQuery} from "@apollo/client";
import {Modal, PrimaryButton, Stack, Text, TextField} from "@fluentui/react";
import {useTranslation} from "react-i18next";
import {DropdownFromList} from "./DropdownFromList";
import {theme} from "../index";
import {ShowApiError} from "./ShowApiError";
import {useErrorMessage} from "../hooks/useErrorMessage";

interface IPagedGraphQLPicker {
    query: DocumentNode;
    queryKey: string;
    queryResultKey: string;
    queryResultName: string;
    value: any;
    setValue: (a: any) => void;
    errorMessage?: string;
    disabled?: boolean;
    title?: string;
}

interface IPagedGraphQLPickerModalContent {
    query: DocumentNode;
    queryKey: string;
    queryResultKey: string;
    queryResultName: string;
    value: any;
    setValue: (a: any) => void;
    search?: string;
    disabled?: boolean;
}

const PagedGraphQLPickerModalContent: FC<IPagedGraphQLPickerModalContent> = ({
                                                                                 query,
                                                                                 queryResultKey,
                                                                                 queryResultName,
                                                                                 queryKey,
                                                                                 setValue,
                                                                                 search,
                                                                                 value,
                                                                                 disabled,
                                                                             }) => {
    const {t} = useTranslation();
    const {loading, error, data, fetchMore} = useQuery(query, {
        variables: {search},
    });
    if (loading && !data)
        return (
            <Stack horizontal horizontalAlign={"center"}>
                <Text>{t("loading")}</Text>
            </Stack>
        );
    if (error)
        return (
            <Stack horizontal horizontalAlign={"center"}>
                <Text>
                    {t("error")} {error.message}
                </Text>
            </Stack>
        );

    return (
        <Stack tokens={{childrenGap: 10, padding: "0 0 2px 0"}}>
            <DropdownFromList<any>
                toKND={(t) => ({
                    data: t,
                    key: t[queryResultKey],
                    name: t[queryResultName],
                })}
                list={data[queryKey]["nodes"]}
                disabled={disabled}
                setValue={setValue}
                value={value}
                inline
            />
            {data[queryKey]["pageInfo"]["hasNextPage"] ? (
                <PrimaryButton
                    onClick={() =>
                        fetchMore({
                            query,
                            variables: {
                                search,
                                cursor: data[queryKey]["pageInfo"]["endCursor"],
                            } as any,
                            updateQuery: (previousQueryResult, {fetchMoreResult}) => {
                                if (!fetchMoreResult) return previousQueryResult;
                                fetchMoreResult[queryKey]["nodes"] = [
                                    ...previousQueryResult[queryKey]["nodes"],
                                    ...fetchMoreResult[queryKey]["nodes"],
                                ];
                                return fetchMoreResult;
                            },
                        })
                    }
                >
                    Učitaj više
                </PrimaryButton>
            ) : (
                <Stack
                    horizontal
                    horizontalAlign={"center"}
                    tokens={{padding: "0 0 10px 0"}}
                >
                    <Text variant={"small"}>Kraj liste</Text>
                </Stack>
            )}
        </Stack>
    );
};

export const PagedGraphQLPicker: FC<IPagedGraphQLPicker> = ({
                                                                query,
                                                                queryKey,
                                                                queryResultKey,
                                                                queryResultName,
                                                                value,
                                                                setValue,
                                                                errorMessage,
                                                                disabled,
                                                                title,
                                                            }) => {
    const [search, setSearch] = useState<string>();
    const [showPickerModal, setShowPickerModal] = useState<boolean>();
    const [graphQLSearch, setGraphQLSearch] = useState<string>();
    const timer = useRef<number>();
    const {t} = useTranslation();
    const {localErrorMessage, clearLocalErrorMessage} =
        useErrorMessage(errorMessage);

    useEffect(() => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = window.setTimeout(() => {
            setGraphQLSearch(search);
        }, 300);
    }, [search]);

    return (
        <Stack tokens={{childrenGap: 10}}>
            <Stack
                horizontal
                tokens={{childrenGap: 10}}
                horizontalAlign={"space-between"}
                verticalAlign={"center"}
            >
                {value ? (
                    <Stack tokens={{childrenGap: 5}}>
                        <Text>{queryResultName && value && value[queryResultName]}</Text>
                        <Text
                            variant={"small"}
                            style={{color: theme.palette?.themePrimary}}
                        >
                            {queryResultKey && value && value[queryResultKey]}
                        </Text>
                    </Stack>
                ) : (
                    <Stack>
                        <Text> - Nije odabrano - </Text>
                    </Stack>
                )}
                <PrimaryButton onClick={() => setShowPickerModal(true)}>
                    Odabir
                </PrimaryButton>
                <Modal
                    isOpen={showPickerModal}
                    onDismiss={() => setShowPickerModal(false)}
                >
                    <TextField
                        placeholder={t("search")}
                        value={search}
                        onFocus={(e) => clearLocalErrorMessage()}
                        onChange={(event, newValue) => setSearch(newValue)}
                    />
                    <PagedGraphQLPickerModalContent
                        value={value}
                        setValue={(v) => {
                            setShowPickerModal(false);
                            setValue(v);
                        }}
                        disabled={disabled}
                        queryResultName={queryResultName}
                        queryResultKey={queryResultKey}
                        query={query}
                        queryKey={queryKey}
                        search={graphQLSearch}
                    />
                </Modal>
            </Stack>
            {localErrorMessage ? (
                <ShowApiError errors={{"": [localErrorMessage]}} keys={[""]}/>
            ) : null}
        </Stack>
    );
};
